import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/src/style.css';
import { findPreset, presetsForColumn, computeRangeFromColumns } from './presets';
import './InspectDateRange.css';

var moment = require('moment');

var POPOVER_WIDTH = 440;
var POPOVER_OFFSET = 6;
var VIEWPORT_MARGIN = 8;
var POPOVER_ID = 'inspect-date-range-popover';

function isCustomActive(value) {
  return !!(value && (value.customFrom || value.customTo));
}

function formatDateSpan(t, from, to) {
  var fmt = 'MMM D';
  if (from && to) {
    var fromStr = from.format(fmt);
    var toStr = to.format(fmt);
    if (from.year() !== to.year()) {
      fromStr = from.format('MMM D, YYYY');
      toStr = to.format('MMM D, YYYY');
    }
    return fromStr + ' \u2013 ' + toStr;
  }
  if (from) return t('inspect:dateRangeFromPrefix', { date: from.format(fmt) });
  if (to) return t('inspect:dateRangeUntilPrefix', { date: to.format(fmt) });
  return t('inspect:dateRangeAllTime');
}

function formatPrimaryLabel(t, value) {
  if (!value) return t('inspect:dateRangeAllTime');
  if (isCustomActive(value)) return t('inspect:dateRangeCustomRange');
  var past = value.past || 'all';
  var future = value.future || 'all';
  if (past === 'all' && future === 'all') return t('inspect:dateRangeAllTime');
  var p = past !== 'all' ? findPreset(past) : null;
  var f = future !== 'all' ? findPreset(future) : null;
  var pastLabel = p ? t(p.labelKey) : t('inspect:dateRangeAll');
  var futureLabel = f ? t(f.labelKey) : t('inspect:dateRangeAll');
  return pastLabel + ' \u00B7 ' + futureLabel;
}

function formatSecondaryLabel(t, value) {
  if (!value) return null;
  if (isCustomActive(value)) {
    return formatDateSpan(t, value.customFrom, value.customTo);
  }
  var past = value.past || 'all';
  var future = value.future || 'all';
  if (past === 'all' && future === 'all') return null;
  return formatDateSpan(t, value.from, value.to);
}

function momentToInputValue(m) {
  if (!m) return '';
  return m.format('YYYY-MM-DD');
}

function inputValueToDate(str) {
  if (!str || str.length !== 10) return null;
  var d = new Date(str + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  return d;
}

class InspectDateRange extends Component {
  constructor(props) {
    super(props);
    var v = props.value;
    var custom = isCustomActive(v);
    this.state = {
      isOpen: false,
      anchorRect: null,
      anchorAbove: false,
      customExpanded: custom,
      customFromValue: custom ? momentToInputValue(v && v.customFrom) : '',
      customToValue: custom ? momentToInputValue(v && v.customTo) : '',
      activeCustomField: 'from',
    };
    this.triggerRef = React.createRef();
    this.popoverRef = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isOpen && !prevState.isOpen) {
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('mousedown', this.handleDocumentMouseDown);
      window.addEventListener('scroll', this.handleReposition, true);
      window.addEventListener('resize', this.handleReposition);
    } else if (!this.state.isOpen && prevState.isOpen) {
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('mousedown', this.handleDocumentMouseDown);
      window.removeEventListener('scroll', this.handleReposition, true);
      window.removeEventListener('resize', this.handleReposition);
      if (this.triggerRef.current) this.triggerRef.current.focus();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    window.removeEventListener('scroll', this.handleReposition, true);
    window.removeEventListener('resize', this.handleReposition);
  }

  _measureAnchor() {
    if (!this.triggerRef.current) return { rect: null, above: false };
    var rect = this.triggerRef.current.getBoundingClientRect();
    var popoverHeight = this.popoverRef.current
      ? this.popoverRef.current.offsetHeight
      : 400;
    var spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    var spaceAbove = rect.top - VIEWPORT_MARGIN;
    var above = spaceBelow < popoverHeight + POPOVER_OFFSET && spaceAbove > spaceBelow;
    return { rect: rect, above: above };
  }

  handleTriggerClick = () => {
    if (this.state.isOpen) {
      this.handleClose();
      return;
    }
    var measured = this._measureAnchor();
    var v = this.props.value;
    var custom = isCustomActive(v);
    this.setState({
      isOpen: true,
      anchorRect: measured.rect,
      anchorAbove: measured.above,
      customFromValue: custom ? momentToInputValue(v && v.customFrom) : '',
      customToValue: custom ? momentToInputValue(v && v.customTo) : '',
      customExpanded: custom,
      activeCustomField: 'from',
    });
  };

  handleClose = () => {
    this.setState({ isOpen: false });
  };

  handleKeyDown = (e) => {
    if (e.key === 'Escape') this.handleClose();
  };

  handleDocumentMouseDown = (e) => {
    if (!this.popoverRef.current) return;
    if (this.popoverRef.current.contains(e.target)) return;
    if (this.triggerRef.current && this.triggerRef.current.contains(e.target)) return;
    this.handleClose();
  };

  handleReposition = () => {
    if (!this.state.isOpen) return;
    var measured = this._measureAnchor();
    this.setState({ anchorRect: measured.rect, anchorAbove: measured.above });
  };

  render() {
    var t = this.props.t;
    var value = this.props.value;
    // Trigger lines are swapped: the concrete date span is the prominent title
    // and the preset label ("Last 7 Days  Next 7 Days") drops to the subtext.
    // With no span (all-time / no selection) the preset label stands alone.
    var presetLabel = formatPrimaryLabel(t, value);
    var spanLabel = formatSecondaryLabel(t, value);
    var primaryText = spanLabel ? spanLabel : presetLabel;
    var secondaryText = spanLabel ? presetLabel : null;
    var isOpen = this.state.isOpen;

    return (
      <React.Fragment>
        <button
          ref={this.triggerRef}
          type="button"
          className={'inspect-date-range-trigger' + (isOpen ? ' open' : '') + (secondaryText ? ' has-sub' : '')}
          aria-haspopup="dialog"
          aria-expanded={isOpen ? 'true' : 'false'}
          aria-controls={POPOVER_ID}
          onClick={this.handleTriggerClick}
        >
          <span className="material-symbols-outlined inspect-date-range-icon">date_range</span>
          <span className="inspect-date-range-labels">
            <span className="inspect-date-range-label-primary">{primaryText}</span>
            {secondaryText && (
              <span className="inspect-date-range-label-secondary">{secondaryText}</span>
            )}
          </span>
        </button>
        {isOpen && this.renderPopover()}
      </React.Fragment>
    );
  }

  renderPopover() {
    var t = this.props.t;
    var className = 'inspect-date-range-popover' + (this.state.anchorAbove ? ' anchor-above' : '');
    return ReactDOM.createPortal(
      <div
        id={POPOVER_ID}
        className={className}
        ref={this.popoverRef}
        role="dialog"
        aria-label={t('inspect:dateRangeDialogLabel')}
        style={this._popoverStyle()}
      >
        <div className="inspect-date-range-cols">
          {this.renderColumn('past')}
          {this.renderColumn('future')}
        </div>
        {this.renderCustomRange()}
      </div>,
      document.body
    );
  }

  _popoverStyle() {
    var rect = this.state.anchorRect;
    if (!rect) return { width: POPOVER_WIDTH };
    var left = Math.max(VIEWPORT_MARGIN, rect.right - POPOVER_WIDTH);
    if (left + POPOVER_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
    }
    var style = { width: POPOVER_WIDTH, left: left };
    if (this.state.anchorAbove) {
      style.bottom = window.innerHeight - rect.top + POPOVER_OFFSET;
    } else {
      style.top = rect.bottom + POPOVER_OFFSET;
    }
    return style;
  }

  _columnValue(column) {
    var v = this.props.value;
    if (!v) return 'all';
    if (column === 'past') return v.past || 'all';
    return v.future || 'all';
  }

  _isColumnKeyActive(column, key) {
    if (isCustomActive(this.props.value)) return false;
    return this._columnValue(column) === key;
  }

  _emitColumnChange(column, nextKey) {
    var v = this.props.value || {};
    var past = isCustomActive(v) ? 'all' : (v.past || 'all');
    var future = isCustomActive(v) ? 'all' : (v.future || 'all');
    if (column === 'past') past = nextKey;
    else future = nextKey;
    var range = computeRangeFromColumns(past, future);
    this.props.onChange({
      from: range.from,
      to: range.to,
      past: past,
      future: future,
      customFrom: null,
      customTo: null,
    });
    this.setState({
      customExpanded: false,
      customFromValue: '',
      customToValue: '',
      activeCustomField: 'from',
    });
  }

  renderColumn(column) {
    var self = this;
    var t = this.props.t;
    var presets = presetsForColumn(column);
    var sub = column === 'past'
      ? t('inspect:dateRangePastSub')
      : t('inspect:dateRangeFutureSub');
    var title = column === 'past'
      ? t('inspect:dateRangePast')
      : t('inspect:dateRangeFuture');
    var allActive = self._isColumnKeyActive(column, 'all');
    return (
      <div className={'inspect-date-range-col inspect-date-range-col-' + column}>
        <div className="inspect-date-range-col-header">
          <div className="inspect-date-range-stip-slot">
            {column === 'past' && this.renderStipple(false)}
          </div>
          <span className="inspect-date-range-col-title">{title}</span>
          <div className="inspect-date-range-stip-slot">
            {column === 'future' && this.renderStipple(true)}
          </div>
        </div>
        <div className="inspect-date-range-col-sub">{sub}</div>
        <div className="inspect-date-range-presets">
          <button
            type="button"
            className={'inspect-date-range-preset inspect-date-range-preset-col-all' + (allActive ? ' active' : '')}
            onClick={function () { self._emitColumnChange(column, 'all'); }}
          >
            {t('inspect:dateRangeAll')}
          </button>
          {presets.map(function (p) {
            var isActive = self._isColumnKeyActive(column, p.key);
            return (
              <button
                key={p.key}
                type="button"
                className={'inspect-date-range-preset' + (isActive ? ' active' : '')}
                onClick={function () { self._emitColumnChange(column, p.key); }}
              >
                {t(p.labelKey)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  _emitCustomRange(fromMoment, toMoment) {
    // Swap if the user entered an inverted range so downstream filters
    // don't silently produce an empty result set.
    if (fromMoment && toMoment && fromMoment.isAfter(toMoment, 'day')) {
      var tmp = fromMoment;
      fromMoment = toMoment;
      toMoment = tmp;
      this.setState({
        customFromValue: momentToInputValue(fromMoment),
        customToValue: momentToInputValue(toMoment),
      });
    }
    this.props.onChange({
      from: fromMoment,
      to: toMoment,
      past: 'all',
      future: 'all',
      customFrom: fromMoment,
      customTo: toMoment,
    });
  }

  handleCustomExpand = () => {
    this.setState({ customExpanded: true, activeCustomField: 'from' });
  };

  handleCustomFromChange = (e) => {
    var val = e.target.value;
    this.setState({ customFromValue: val });
    this._commitCustomRange(val, this.state.customToValue);
  };

  handleCustomToChange = (e) => {
    var val = e.target.value;
    this.setState({ customToValue: val });
    this._commitCustomRange(this.state.customFromValue, val);
  };

  handleCustomFromFocus = () => {
    this.setState({ customExpanded: true, activeCustomField: 'from' });
  };

  handleCustomToFocus = () => {
    this.setState({ customExpanded: true, activeCustomField: 'to' });
  };

  _commitCustomRange(fromStr, toStr) {
    var fromDate = inputValueToDate(fromStr);
    var toDate = inputValueToDate(toStr);
    var fromMoment = fromDate ? moment(fromDate) : null;
    var toMoment = toDate ? moment(toDate) : null;
    if (fromStr && !fromDate) return;
    if (toStr && !toDate) return;
    this._emitCustomRange(fromMoment, toMoment);
  }

  handleDaySelect = (range) => {
    if (!range) return;
    var active = this.state.activeCustomField;
    var self = this;
    function fmt(d) {
      if (!d) return '';
      return [
        d.getFullYear(),
        ('0' + (d.getMonth() + 1)).slice(-2),
        ('0' + d.getDate()).slice(-2),
      ].join('-');
    }
    if (active === 'from') {
      var fromDate = range.from ? range.from : (range.to ? range.to : null);
      var newFrom = fmt(fromDate);
      self.setState({ customFromValue: newFrom, activeCustomField: 'to' });
      self._commitCustomRange(newFrom, self.state.customToValue);
    } else {
      var toDate = range.to ? range.to : (range.from ? range.from : null);
      var newTo = fmt(toDate);
      self.setState({ customToValue: newTo, activeCustomField: 'from' });
      self._commitCustomRange(self.state.customFromValue, newTo);
    }
  };

  renderCustomRange() {
    var t = this.props.t;
    var state = this.state;
    var isExpanded = state.customExpanded;
    var fromDay = inputValueToDate(state.customFromValue);
    var toDay = inputValueToDate(state.customToValue);
    var selectedRange = null;
    if (fromDay || toDay) selectedRange = { from: fromDay, to: toDay };
    var defaultMonth = fromDay || toDay || new Date();
    var placeholder = t('inspect:dateRangeInputPlaceholder');

    return (
      <div className={'inspect-date-range-custom' + (isExpanded ? ' expanded' : '')}>
        <button
          type="button"
          className="inspect-date-range-custom-label"
          onClick={this.handleCustomExpand}
        >
          {t('inspect:dateRangeCustomRange')}
        </button>
        <div className="inspect-date-range-custom-row">
          <input
            type="text"
            className={'inspect-date-range-custom-input' + (state.activeCustomField === 'from' && isExpanded ? ' active-field' : '')}
            placeholder={placeholder}
            value={state.customFromValue}
            onChange={this.handleCustomFromChange}
            onFocus={this.handleCustomFromFocus}
          />
          <span className="inspect-date-range-custom-dash">&#x2013;</span>
          <input
            type="text"
            className={'inspect-date-range-custom-input' + (state.activeCustomField === 'to' && isExpanded ? ' active-field' : '')}
            placeholder={placeholder}
            value={state.customToValue}
            onChange={this.handleCustomToChange}
            onFocus={this.handleCustomToFocus}
          />
        </div>
        {isExpanded && (
          <div className="inspect-date-range-calendar">
            <DayPicker
              mode="range"
              selected={selectedRange}
              onSelect={this.handleDaySelect}
              captionLayout="dropdown"
              fromYear={2015}
              toYear={2035}
              defaultMonth={defaultMonth}
              showOutsideDays={false}
              fixedWeeks={false}
            />
          </div>
        )}
      </div>
    );
  }

  renderStipple(mirror) {
    // Deterministic 2-row x 8-col density matrix. Density grows toward the
    // word; mirror=true reverses columns cell-for-cell so Past and Future
    // stipples are perfectly symmetric around the title.
    var DENSITY = [
      [0.00, 0.00, 0.18, 0.35, 0.55, 0.00, 0.85, 1.00],
      [0.00, 0.15, 0.00, 0.45, 0.00, 0.75, 0.95, 1.00],
    ];
    var dots = [];
    for (var r = 0; r < DENSITY.length; r++) {
      for (var c = 0; c < DENSITY[r].length; c++) {
        var col = mirror ? (DENSITY[r].length - 1 - c) : c;
        dots.push(
          <div
            key={r + '-' + c}
            className="inspect-date-range-stip-dot"
            style={{ opacity: DENSITY[r][col].toFixed(2) }}
          />
        );
      }
    }
    return (
      <div className="inspect-date-range-stip" aria-hidden="true">
        {dots}
      </div>
    );
  }
}

InspectDateRange.propTypes = {
  value: PropTypes.shape({
    from: PropTypes.object,
    to: PropTypes.object,
    past: PropTypes.string,
    future: PropTypes.string,
    customFrom: PropTypes.object,
    customTo: PropTypes.object,
  }),
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default InspectDateRange;
